import { useMemo } from 'react'
import { initialOfferPageRooms } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferAvailableRates from 'hooks/useOfferAvailableRates'
import type { BaseRatePackageView } from 'selectors/offerPage/offerPageSelectors'
import { getAlwaysOnMobileRateAvailability, getAlwaysOnMobileRateValue } from './alwaysOnMobileRateUtils'

interface Params {
  offer: App.Offer
  packageView: App.OfferPackageOptionView
  checkIn?: string
  checkOut?: string
  rooms: Array<App.Occupants>
  calendarsLoading?: boolean
  availableForPurchase?: boolean
  baseRatePackagesView: Array<BaseRatePackageView>
  canViewLuxPlusBenefits: boolean
  previewMode: boolean
}

export default function useAlwaysOnMobileRateDetails({
  offer, packageView, checkIn, checkOut, rooms, calendarsLoading, availableForPurchase,
  baseRatePackagesView, canViewLuxPlusBenefits, previewMode,
}: Params) {
  const pkg = packageView.package
  const offerAvailableRates = useOfferAvailableRates(offer.id, {
    checkIn,
    checkOut,
    rooms: rooms ?? initialOfferPageRooms,
  })
  const pkgAvailableRate = useMemo(() => offerAvailableRates.rates.find(rate => rate.packageUniqueKey === pkg.uniqueKey), [pkg, offerAvailableRates])
  const fetching = !!calendarsLoading || !!offerAvailableRates?.fetching
  const useDynamicCancellationPolicies = offer.property?.useDynamicCancellationPolicies ?? false
  const ignoreDynamicCancellationPolicy = pkg?.ignoreDynamicCancellationPolicy ?? false
  const { hotelAvailable, canPurchase, canBuyNow } = getAlwaysOnMobileRateAvailability(
    packageView, useDynamicCancellationPolicies, !!pkgAvailableRate, checkIn, rooms, fetching, availableForPurchase,
  )
  const baseRatePackageView = useMemo(() => baseRatePackagesView.find(
    packageItem => packageItem.duration === pkg.duration && packageItem.id === pkg.id,
  ), [baseRatePackagesView, pkg.duration, pkg.id])
  const { shouldShowDiscount, valuedUpTo } = useMemo(() => getAlwaysOnMobileRateValue(
    packageView, baseRatePackageView, canViewLuxPlusBenefits, offer.isDiscountPillHidden, canPurchase, previewMode,
  ), [packageView, baseRatePackageView, canViewLuxPlusBenefits, offer.isDiscountPillHidden, canPurchase, previewMode])

  return { pkgAvailableRate, fetching, useDynamicCancellationPolicies, ignoreDynamicCancellationPolicy, hotelAvailable, canPurchase, canBuyNow, shouldShowDiscount, valuedUpTo }
}
